import {
    ChangeDetectorRef,
    Component,
    Directive,
    ElementRef,
    forwardRef,
    Input,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, DefaultValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { FloatLabelType } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import Debug from 'debug';
import { Subject } from 'rxjs';
import { CanSize, CanSizeCtor, mixinSize } from '../../style/CanSize';

const debug = Debug('aumico:aumico-lib-core-fe:AumFormField');

let nextUniqueId = 0;

class AumFormFieldBase {
    constructor(public _elementRef: ElementRef) {}
}
const _AumFormFieldMixinBase: CanSizeCtor & typeof AumFormFieldBase = mixinSize(AumFormFieldBase, 'medium');

/**
 * to overwrite the with you have to change the css of .mat-form-field-infix
 */
@Component({
    selector: 'aum-form-field',
    styleUrls: ['./aum-form-field.scss', './aum-form-field-outline.scss', './aum-input.scss'],
    templateUrl: './aum-form-field.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AumFormField),
            multi: true,
        },
    ],
    inputs: ['size'],
    host: {
        class: 'aum-form-field',
    },
})
export class AumFormField extends _AumFormFieldMixinBase implements ControlValueAccessor, CanSize {
    @Input()
    placeholder: string;

    /** Less space because no space needed for Error message and Floating Label */
    @Input()
    simpleInput?: boolean = false;

    @Input() fullWidth: boolean = false;

    @Input()
    get aumFormControl(): UntypedFormControl {
        return this._aumFormControl !== undefined ? this._aumFormControl : this._internalFormControl;
    }
    set aumFormControl(value: UntypedFormControl) {
        if (value !== this._aumFormControl) {
            this._aumFormControl = value;
            this._changeDetectorRef.markForCheck();
        }
    }
    private _aumFormControl: UntypedFormControl;
    private _internalFormControl: UntypedFormControl = new UntypedFormControl({ value: '', disabled: false }, []);

    @ViewChild(DefaultValueAccessor, { static: true }) valueAccessor: DefaultValueAccessor;
    protected _uid = `aum-input-${nextUniqueId++}`;
    readonly stateChanges: Subject<void> = new Subject<void>();
    /**
     * Implemented as part of MatFormFieldControl.
     * @docs-private
     */
    focused: boolean = false;

    /**
     * Implemented as part of MatFormFieldControl.
     * @docs-private
     */
    @Input()
    get disabled(): boolean {
        return this.aumFormControl.disabled;
    }
    set disabled(value: boolean | undefined) {
        debug('SetDisabled', value);
        debug(value);
        if (value === false || value == null) {
            this.aumFormControl.enable();
        } else {
            this.aumFormControl.disable();
        }

        // Browsers may not fire the blur event if the input is disabled too quickly.
        // Reset from here to ensure that the element doesn't become stuck.
        if (this.focused) {
            this.focused = false;
            this.stateChanges.next();
        }
    }

    /**
     * Implemented as part of MatFormFieldControl.
     * @docs-private
     */
    @Input()
    get id(): string {
        return this._id;
    }
    set id(value: string) {
        this._id = value || this._uid;
    }
    protected _id: string;

    /**
     * Whether the label should always float, never float or float as the user types.
     *
     * Note: only the legacy appearance supports the `never` option. `never` was originally added as a
     * way to make the floating label emulate the behavior of a standard input placeholder. However
     * the form field now supports both floating labels and placeholders. Therefore in the non-legacy
     * appearances the `never` option has been disabled in favor of just using the placeholder.
     */
    @Input()
    get floatLabel(): FloatLabelType {
        return this._floatLabel === 'never' ? 'auto' : this._floatLabel;
    }
    set floatLabel(value: FloatLabelType) {
        if (value !== this._floatLabel) {
            this._floatLabel = value || 'auto';
            this._changeDetectorRef.markForCheck();
        }
    }
    private _floatLabel: FloatLabelType;

    constructor(
        el: ElementRef,
        protected _changeDetectorRef: ChangeDetectorRef,
        protected readonly renderer: Renderer2,
        protected matIconRegistry: MatIconRegistry,
        protected domSanitizer: DomSanitizer,
    ) {
        super(el);
        this.addSvgIcon('aumico-pattern-icon-alert-triangle');
        this.floatLabel = 'always';
    }

    writeValue(obj: any) {
        this.valueAccessor.writeValue(obj);
    }

    registerOnChange(fn: any) {
        this.valueAccessor.registerOnChange(fn);
    }

    registerOnTouched(fn: any) {
        this.valueAccessor.registerOnTouched(fn);
    }

    setDisabledState(isDisabled: boolean) {
        this.valueAccessor.setDisabledState(isDisabled);
    }

    protected addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}

/**
 * `<aum-form-field-label>`
 *
 * This directive is to be used inside of the AumFormField component.
 */
@Directive({
    selector: 'aum-form-field-label',
    host: {
        class: 'aum-form-field-label',
    },
})
export class AumFormFieldLabel {}
/**
 * `<aum-form-field-label>`
 *
 * This directive is to be used inside of the AumFormField component.
 */
@Directive({
    selector: 'aum-form-error',
    host: {
        class: 'aum-form-error',
    },
})
export class AumFormError {}
